import { Box, Image } from '@mantine/core';
import { useRevokableObjectUrl } from '../../shared/hooks/useRevokableObjectUrl';
import { itemImageObjectUrl, type ItemImageDto } from './api';

interface Props {
  itemId: string;
  image: ItemImageDto;
  height: number;
}

/**
 * One item photo, fetched as a blob and shown from an object URL.
 *
 * Not an `<img src>` pointing at the API. An img tag issues a plain GET: it would carry the
 * session cookie, but it CANNOT carry X-Warehouse-Id, and every warehouse-scoped endpoint refuses
 * a request without it. Exempting the image endpoint from scoping would have made a bare img work
 * — and would also have let one 3PL client's staff read a photo of another client's stock.
 *
 * A component per photo so each one owns exactly one object URL and revokes it on unmount.
 */
export function ItemPhotoThumbnail({ itemId, image, height }: Props) {
  const url = useRevokableObjectUrl(
    (signal) => itemImageObjectUrl(itemId, image.itemImageId, signal),
    [itemId, image.itemImageId],
  );

  // The placeholder covers three states with one appearance — loading, forbidden, and gone — on
  // purpose. A per-tile spinner flickers, and a per-tile error is noise for something the
  // operator can see is missing.
  if (!url) return <Box h={height} bg="var(--mantine-color-default-hover)" />;

  // alt is empty deliberately: the file name beside it already names the photo, and a screen
  // reader announcing it twice is worse than not announcing the image at all.
  return <Image src={url} h={height} fit="contain" alt="" />;
}
